import React from "react";
import katex from "katex";
import "katex/dist/katex.min.css";

/**
 * Render a LaTeX source string via KaTeX safely.
 * - Trims whitespace.
 * - `throwOnError: false` -> KaTeX prints the offending source in red instead of crashing.
 * - We ALSO wrap it in try/catch: if KaTeX itself throws, we render a friendly Turkish error.
 */
function renderPart(part, block) {
  const src = String(part ?? "").trim();
  if (!src) return "";
  try {
    return katex.renderToString(src, {
      throwOnError: false,
      displayMode: block,
      errorColor: "#E11D48",
      strict: "ignore",
      output: "html",
    });
  } catch (err) {
    const safe = src.replace(/</g, "&lt;");
    return `<span style="color:#E11D48;font-family:monospace">LaTeX hatası: ${safe}</span>`;
  }
}

export function InlineMath({ children }) {
  const html = renderPart(children, false);
  return <span data-testid="katex-inline" dangerouslySetInnerHTML={{ __html: html }} />;
}

export function BlockMath({ children }) {
  const html = renderPart(children, true);
  return <div data-testid="katex-block" dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * Auto-parse text with $...$ inline and $$...$$ block math.
 * Used ONLY for inline math inside legacy plain-text; NOT called from the
 * dedicated "Matematik (LaTeX)" block (that goes straight to BlockMath).
 */
export function RichMath({ text }) {
  if (!text) return null;
  const nodes = [];
  const regex = /(\$\$[\s\S]+?\$\$|\$[^\n$]+?\$)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(<span key={key++}>{text.slice(last, m.index)}</span>);
    const raw = m[0];
    if (raw.startsWith("$$")) {
      nodes.push(<BlockMath key={key++}>{raw.slice(2, -2)}</BlockMath>);
    } else {
      nodes.push(<InlineMath key={key++}>{raw.slice(1, -1)}</InlineMath>);
    }
    last = m.index + raw.length;
  }
  if (last < text.length) nodes.push(<span key={key++}>{text.slice(last)}</span>);
  return <>{nodes}</>;
}
